import React, { useEffect, useState, memo } from "react";
import PropTypes from "prop-types";
import Select from "react-select";

// Extracted LoadingIndicator component
const LoadingIndicator = ({
  className = "animate-spin h-4 w-4 text-blue-600",
}) => (
  <div className="flex items-center justify-center p-2">
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  </div>
);

LoadingIndicator.propTypes = {
  className: PropTypes.string,
};

LoadingIndicator.defaultProps = {
  className: "animate-spin h-4 w-4 text-blue-600",
};

const Dropdown = ({
  options,
  submitRef,
  defaultValue,
  required,
  errorMessage,
  handleChange,
  defaultLabel,
  multiple,
  disabled,
  onMenuClose,
  onMenuOpen,
  label,
  className,
  requiredField,
  errorDivCustomStyles = "",
  keyLabel = "label",
  keyValue = "value",
  keyLabel2 = null,
  message,
  isClearable,
  menuPlacement = "bottom",
  size = "medium", // small, medium, large
  id,
}) => {
  const [error, setError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (submitRef && isEmptyObject(defaultValue) && required) {
      setError(true);
    }
  }, [errorMessage, error, submitRef, options, defaultValue, required]);

  const onchangeinput = (e) => {
    handleChange(e);
    setError(false);
  };

  function isEmptyObject(obj) {
    return obj === null || Object.keys(obj).length === 0;
  }

  const dropdownBlurred = () => {
  setIsFocused(false);
  if (!isEmptyObject(defaultValue)) {
    setError(false);
  }
   // Add any additional blur logic here if needed
};

  const dropdownFocused = () => {
    setIsFocused(true);
  };

  // Size configurations
  const sizeConfig = {
    small: {
      minHeight: "36px",
      fontSize: "14px",
      padding: "4px 8px",
    },
    medium: {
      minHeight: "44px",
      fontSize: "16px",
      padding: "8px 12px",
    },
    large: {
      minHeight: "52px",
      fontSize: "18px",
      padding: "12px 16px",
    },
  };

  const customStyles = {
    control: (provided, state) => {
      // Extract border color logic
      let borderColor;
      if (errorMessage) {
        borderColor = "#ef4444";
      } else if (state.isFocused) {
        borderColor = "#3b82f6";
      } else {
        borderColor = "#d1d5db";
      }

      // Extract box shadow logic
      let boxShadow;
      if (state.isFocused) {
        if (errorMessage) {
          boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.1)";
        } else {
          boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }
      } else {
        boxShadow = "none";
      }

      // Extract hover border color logic
      let hoverBorderColor;
      if (errorMessage) {
        hoverBorderColor = "#ef4444";
      } else if (state.isFocused) {
        hoverBorderColor = "#3b82f6";
      } else {
        hoverBorderColor = "#9ca3af";
      }

      return {
        ...provided,
        minHeight: sizeConfig[size].minHeight,
        border: `2px solid ${borderColor}`,
        borderRadius: "8px",
        backgroundColor: disabled ? "#f3f4f6" : "#ffffff",
        boxShadow,
        transition: "all 0.2s ease-in-out",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": {
          borderColor: hoverBorderColor,
        },
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      padding: sizeConfig[size].padding,
      fontSize: sizeConfig[size].fontSize,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
      fontSize: sizeConfig[size].fontSize,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#374151",
      fontSize: sizeConfig[size].fontSize,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e0e7ff",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#3730a3",
      fontSize: "14px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6366f1",
      "&:hover": {
        backgroundColor: "#c7d2fe",
        color: "#4338ca",
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow:
        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      border: "1px solid #e5e7eb",
      maxHeight: "200px",
      overflowY: "auto",
      zIndex: 99999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
      padding: "4px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    option: (provided, state) => {
      // Extract background color logic
      let backgroundColor;
      if (state.isSelected) {
        backgroundColor = "#3b82f6";
      } else if (state.isFocused) {
        backgroundColor = "#eff6ff";
      } else {
        backgroundColor = "transparent";
      }

      // Extract hover background color logic
      const hoverBackgroundColor = state.isSelected ? "#3b82f6" : "#eff6ff";

      return {
        ...provided,
        backgroundColor,
        color: state.isSelected ? "#ffffff" : "#374151",
        borderRadius: "6px",
        margin: "2px 0",
        padding: "8px 12px",
        cursor: "pointer",
        fontSize: "14px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: hoverBackgroundColor,
        },
      };
    },
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "#3b82f6" : "#9ca3af",
      transition: "all 0.2s ease-in-out",
      transform: state.selectProps.menuIsOpen
        ? "rotate(180deg)"
        : "rotate(0deg)",
      "&:hover": {
        color: "#3b82f6",
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: "#9ca3af",
      "&:hover": {
        color: "#ef4444",
      },
    }),
  };

  const getLabelClasses = () => {
    const baseClasses =
      "block text-sm font-semibold mb-1.5 transition-colors duration-200";
    let colorClasses;
    if (errorMessage) {
      colorClasses = "text-[#ef4444]";
    } else if (isFocused) {
      colorClasses = "text-[#3b82f6]";
    } else {
      colorClasses = "text-gray-700 dark:text-gray-300";
    }
    return `${baseClasses} ${colorClasses}`;
  };

  return (
    <div className="relative mb-2 w-full group">
      {label && (
        <label className={getLabelClasses()}>
          <span className="flex items-center gap-1">
            {label}
            {(required || requiredField) && (
              <span className="text-red-500 text-base">*</span>
            )}
          </span>
        </label>
      )}

      <div className="relative">
        <Select
          inputId={id}
          getOptionLabel={(e) =>
            `${e[keyLabel]} ${keyLabel2 ? e[keyLabel2] : ""}`.trim()
          }
          getOptionValue={(e) => e[keyValue]}
          onMenuOpen={() => {
            dropdownFocused();
            onMenuOpen?.();
          }}
          onMenuClose={() => {
            dropdownBlurred();
            onMenuClose?.();
          }}
          placeholder={
            <div className="select-placeholder-text">
              {defaultLabel || "Select an option..."}
            </div>
          }
          isMulti={multiple}
          isDisabled={disabled}
          options={options}
          value={isEmptyObject(defaultValue) ? null : defaultValue}
          onChange={onchangeinput}
          onBlur={dropdownBlurred}
          onFocus={dropdownFocused}
          required={required}
          styles={customStyles}
          className={`${className} react-select-container`}
          classNamePrefix="react-select"
          isClearable={isClearable}
          menuPlacement={menuPlacement}
          isSearchable={true}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          components={{
            LoadingIndicator: LoadingIndicator,
          }}
        />
      </div>

      {/* Enhanced error/message display */}
      <div
        className={`mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs transition-all duration-200 ${
          errorMessage ? "text-[#ef4444]" : "text-[#3b82f6]"
        } ${
          errorMessage || message ? "opacity-100" : "opacity-0"
        } ${errorDivCustomStyles}`}
      >
        {errorMessage && (
          <svg
            className="w-4 h-4 text-red-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {message && !errorMessage && (
          <svg
            className="w-4 h-4 text-blue-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span>{errorMessage || message}</span>
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  // Data props
  options: PropTypes.array.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

  // Event handlers
  handleChange: PropTypes.func.isRequired,
  onMenuClose: PropTypes.func,
  onMenuOpen: PropTypes.func,

  // Validation props
  submitRef: PropTypes.bool,
  required: PropTypes.bool,
  requiredField: PropTypes.bool,
  errorMessage: PropTypes.string,

  // Display props
  defaultLabel: PropTypes.string,
  label: PropTypes.string,
  message: PropTypes.string,
  className: PropTypes.string,
  errorDivCustomStyles: PropTypes.string,

  // Behavior props
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,

  // Configuration props
  keyLabel: PropTypes.string,
  keyValue: PropTypes.string,
  keyLabel2: PropTypes.string,
  menuPlacement: PropTypes.oneOf(["auto", "bottom", "top"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  id: PropTypes.string,
};

Dropdown.defaultProps = {
  defaultValue: null,
  submitRef: false,
  required: false,
  requiredField: false,
  errorMessage: undefined,
  defaultLabel: undefined,
  label: undefined,
  message: undefined,
  className: undefined,
  errorDivCustomStyles: "",
  multiple: false,
  disabled: false,
  isClearable: false,
  onMenuClose: undefined,
  onMenuOpen: undefined,
  keyLabel: "label",
  keyValue: "value",
  keyLabel2: null,
  menuPlacement: "bottom",
  size: "medium",
  id: undefined,
};

export default memo(Dropdown);
